import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const featureRows = [
  { icon: 'Video', title: 'Meet-first flow', body: 'Start with audio, video, chat, participant roles, and a clean pre-join check.' },
  { icon: 'Code2', title: 'Shared coding IDE', body: 'Interviewer selects a backend question; candidate codes while everyone sees cursors and edits.' },
  { icon: 'PenTool', title: 'Shared whiteboard', body: 'System design and explanation space with live pointers for every participant.' },
];

const detectionGroups = [
  {
    icon: 'Globe',
    title: 'Browser activity',
    signals: ['Tab switch or window blur', 'Fullscreen exit', 'DevTools-size resize', 'Split-screen resize', 'Multi-monitor hint', 'Print Screen key', 'Right-click in IDE', 'Idle session'],
  },
  {
    icon: 'Brain',
    title: 'Coding behavior',
    signals: ['Paste into editor', 'Multiple paste bursts', 'Rapid bulk typing', 'Large code block without matching keystrokes', 'AI-like typing pattern'],
  },
  {
    icon: 'Eye',
    title: 'Camera vision',
    signals: ['Multiple faces', 'No face for 10 seconds', 'Phone or secondary device in frame', 'Sustained looking away', 'Repeated side glances', 'Linear reading-like movement'],
  },
  {
    icon: 'Mic',
    title: 'Audio integrity',
    signals: ['Secondary voice', 'Sustained background speaker'],
  },
];

const participants = [
  { name: 'Interviewer', role: 'Host', x: '70%', y: '27%', color: 'var(--lp-accent)' },
  { name: 'Candidate', role: 'Coding', x: '42%', y: '62%', color: '#0ea5e9' },
  { name: 'Panelist', role: 'Watching', x: '80%', y: '70%', color: '#f59e0b' },
];

const BusinessInterviewProductSection = () => {
  return (
    <section id="interview-ide" className="relative overflow-hidden border-t border-[var(--lp-line)] bg-white py-24 lg:py-28">
      <div className="lp-grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <p className="font-mono text-[11px] tracking-[0.25em] text-[var(--lp-accent)]">
              FIRST BUSINESS PRODUCT
            </p>
            <h2 className="mt-4 text-3xl leading-tight tracking-tight text-[var(--lp-ink)] sm:text-4xl lg:text-5xl">
              Live coding interviews with a shared IDE and board.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[var(--lp-ink-soft)]">
              Create a private interview link, bring interviewer and candidate into a meet-style room, then open the coding IDE or whiteboard only when the round needs it.
            </p>

            <div className="mt-8 grid gap-4">
              {featureRows.map((item) => (
                <div key={item.title} className="flex gap-4 border-t border-[var(--lp-line)] pt-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--lp-line)] bg-[var(--lp-accent)]/5 text-[var(--lp-accent)]">
                    <Icon name={item.icon} size={18} />
                  </div>
                  <div>
                    <h3 className="text-base text-[var(--lp-ink)]">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-[var(--lp-ink-soft)]">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 border-t border-[var(--lp-line)] pt-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--lp-line)] bg-[var(--lp-accent)]/5 text-[var(--lp-accent)]">
                  <Icon name="ShieldCheck" size={18} />
                </div>
                <div>
                  <h3 className="text-base text-[var(--lp-ink)]">Anti-cheat signals surfaced live to the interviewer.</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--lp-ink-soft)]">
                    Each alert includes severity, confidence, event details, and a trust-score update in the proctor panel.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {detectionGroups.map((group) => (
                  <div key={group.title} className="rounded-xl border border-[var(--lp-line)] bg-[var(--lp-surface)] p-4">
                    <div className="flex items-center gap-2 text-sm text-[var(--lp-ink)]">
                      <Icon name={group.icon} size={16} className="text-[var(--lp-accent)]" />
                      <span>{group.title}</span>
                    </div>
                    <ul className="mt-3 space-y-2">
                      {group.signals.map((signal) => (
                        <li key={signal} className="flex gap-2 text-xs leading-relaxed text-[var(--lp-ink-soft)]">
                          <Icon name="Check" size={13} className="mt-0.5 shrink-0 text-[var(--lp-accent)]" />
                          <span>{signal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="h-12 rounded-full bg-[var(--lp-accent)] px-7 text-sm text-white hover:bg-[var(--lp-accent)]/90"
                iconName="Plus"
                iconPosition="left"
              >
                <Link to="/for-businesses/interviews">Create interview room</Link>
              </Button>
              <a
                href="#pipeline"
                className="inline-flex h-12 items-center justify-center rounded-full border border-[var(--lp-line)] px-6 text-sm text-[var(--lp-ink)] transition-colors hover:bg-[var(--lp-surface)]"
              >
                See hiring pipeline
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-[var(--lp-line)] bg-[var(--lp-contrast-bg)] text-[var(--lp-contrast-text)] shadow-2xl shadow-black/10">
              <div className="flex h-12 items-center justify-between border-b border-white/10 px-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="font-mono text-[10px] text-white/45">prepxai live interview</div>
              </div>

              <div className="grid min-h-[520px] grid-rows-[1fr_auto] lg:min-h-[560px]">
                <div className="grid gap-px bg-white/10 md:grid-cols-[0.72fr_1fr]">
                  <div className="bg-[#0d0f12] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-white/45">QUESTION</span>
                      <span className="rounded-full bg-[var(--lp-accent)]/15 px-2 py-1 font-mono text-[10px] text-[var(--lp-accent)]">Backend</span>
                    </div>
                    <h3 className="text-xl leading-snug text-white">Pair Sum With Constraints</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">
                      Return the two indices whose values sum to target. Explain the tradeoff between sorting and hashing.
                    </p>
                    <div className="mt-5 space-y-2 font-mono text-xs text-white/55">
                      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">Input: [2, 7, 11, 15], 9</div>
                      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">Output: [0, 1]</div>
                    </div>
                  </div>

                  <div className="relative overflow-hidden bg-[#101217] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-white/45">IDE + PRESENCE</span>
                      <span className="text-xs text-white/45">JavaScript</span>
                    </div>
                    <pre className="min-h-[310px] overflow-hidden rounded-xl border border-white/10 bg-black/30 p-4 font-mono text-[12px] leading-6 text-white/70">
{`function twoSum(nums, target) {
  const seen = new Map();

  for (let i = 0; i < nums.length; i++) {
    const pair = target - nums[i];
    if (seen.has(pair)) return [seen.get(pair), i];
    seen.set(nums[i], i);
  }

  return [];
}`}
                    </pre>
                    {participants.map((participant) => (
                      <div
                        key={participant.name}
                        className="absolute flex items-center gap-2 rounded-full border border-white/10 bg-black/70 px-2.5 py-1 text-[11px] text-white shadow-lg"
                        style={{ left: participant.x, top: participant.y, color: participant.color }}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: participant.color }} />
                        <span className="text-white">{participant.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 border-t border-white/10 bg-black px-4 py-4">
                  {[
                    ['Mic', 'Mic'],
                    ['Video', 'Video'],
                    ['MessageSquare', 'Chat'],
                    ['Code2', 'IDE'],
                    ['PenTool', 'Board'],
                    ['Users', 'People'],
                  ].map(([icon, label]) => (
                    <span key={label} className="inline-flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 text-xs text-white/65">
                      <Icon name={icon} size={15} />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BusinessInterviewProductSection;
